<template>
  <div class="balance-sheet">
    <ReportChoices label="As on" :choices="dateChoices" :active="activeDate" @select="filters.date = $event.date" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="grid-template-columns: repeat(2, minmax(160px, 220px))">
        <div class="rp-filter">
          <label>As on date</label>
          <input v-model="filters.date" type="date" class="form-control" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Levels</label>
          <select v-model="filters.depth" class="form-select">
            <option v-for="choice in DEPTH_CHOICES" :key="String(choice.id)" :value="choice.id">{{ choice.text }}</option>
          </select>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-building-warehouse"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Total Assets</div>
          <div class="rp-card-value">{{ formatMoney(summary.assets) }}</div>
          <div class="rp-card-sub">what the business owns</div>
        </div>
      </div>
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-file-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Liabilities</div>
          <div class="rp-card-value">{{ formatMoney(summary.liabilities) }}</div>
          <div class="rp-card-sub">
            {{ summary.debt_ratio !== null ? `${summary.debt_ratio}% of assets` : 'what the business owes' }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-user-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Equity</div>
          <div class="rp-card-value">{{ formatMoney(summary.equity) }}</div>
          <div class="rp-card-sub">
            incl. {{ formatMoney(Number(summary.retained) + Number(summary.this_year)) }} retained profit
          </div>
        </div>
      </div>
      <div class="rp-card" :class="summary.balanced ? 'tone-green' : 'tone-red'">
        <span class="rp-card-icon"><i class="ti" :class="summary.balanced ? 'ti-circle-check' : 'ti-alert-triangle'"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Assets = Liabilities + Equity</div>
          <div class="rp-card-value" :class="summary.balanced ? 'text-success' : 'text-danger'">
            {{ summary.balanced ? 'Balanced' : `Off by ${formatMoney(summary.difference)}` }}
          </div>
          <div class="rp-card-sub">on {{ formatDate(filters.date) }}</div>
        </div>
      </div>
    </div>

    <div class="acc-split" :class="{ 'is-loading': loading }">
      <div v-for="side in sides" :key="side.key" class="rp-table-card">
        <div class="rp-table-head">
          <div class="fw-semibold">{{ side.title }}</div>
          <span class="fw-semibold">{{ formatMoney(side.total) }}</span>
        </div>
        <div class="table-responsive rp-table-wrap">
          <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
          <table class="table rp-table mb-0">
            <thead>
              <tr>
                <th>Particulars</th>
                <th class="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in side.rows" :key="row.key" :class="rowClass(row)">
                <td>
                  <span class="acc-indent" :style="{ width: `${(row.level || 0) * 1.1}rem` }"></span>
                  <button v-if="row.code" type="button" class="acc-link" title="Open this account's ledger" @click="openLedger(row.code)">
                    {{ row.name }}
                  </button>
                  <template v-else>{{ row.name }}</template>
                  <span v-if="row.code" class="acc-code ms-1">{{ row.code }}</span>
                </td>
                <td class="text-end" :class="{ 'text-danger': Number(row.amount) < 0 }">
                  {{ row.kind === 'section' ? '' : formatMoney(row.amount) }}
                </td>
              </tr>
              <tr v-if="!loading && !side.rows.length">
                <td colspan="2"><div class="rp-empty"><i class="ti ti-scale"></i><div class="fw-semibold">Nothing to show</div></div></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>{{ side.footer }}</th>
                <th class="text-end">{{ formatMoney(side.total) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
    <p class="rp-hint mt-2 mb-0">
      Every approved posting of {{ options.store || 'the store' }} up to the date; stock moved to or from another
      store shows as Inter-store transfers. Assets are debit − credit, liabilities and equity credit −
      debit. The books aren't closed, so income less expenses shows under equity: retained profit from before
      {{ summary.year }} and the profit of {{ summary.year }} to date. A negative bank balance is an overdraft.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { localToday } from '../inventory/inventoryReportMixin';
import { addDays } from '../inventory/periodReportMixin';
import accountingReport, { DEPTH_CHOICES } from './accountingReportMixin';

const EMPTY_SUMMARY = {
  assets: 0, liabilities: 0, equity: 0, liabilities_equity: 0, difference: 0, balanced: true, retained: 0,
  this_year: 0, year: new Date().getFullYear(), debt_ratio: null, groups: {},
};

export default {
  name: 'BalanceSheetReport',
  mixins: [inventoryReport, accountingReport],

  reportUrl: apiUrls.accounting_balance_sheet_report.get,
  fileName: 'balance_sheet',
  columns: [],

  data() {
    return {
      DEPTH_CHOICES,
      today: localToday(),
      filters: { date: localToday(), depth: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
    };
  },

  computed: {
    dateChoices() {
      const today = this.today;
      return [
        { key: 'today', label: 'Today', date: today },
        { key: 'last_month', label: 'End of last month', date: addDays(`${today.slice(0, 8)}01`, -1) },
        { key: 'last_year', label: 'End of last year', date: `${Number(today.slice(0, 4)) - 1}-12-31` },
      ];
    },

    activeDate() {
      return this.dateChoices.find((c) => c.date === this.filters.date)?.key || null;
    },

    hasActiveFilters() {
      return this.filters.date !== this.today || this.filters.depth !== null;
    },

    visibleRows() {
      return this.rows;
    },

    sides() {
      const split = this.rows.findIndex((r) => r.kind === 'spacer');
      const left = (split < 0 ? this.rows : this.rows.slice(0, split))
        .filter((r) => r.key !== 'assets-head' && r.key !== 'assets-total');
      const right = split < 0 ? [] : this.rows.slice(split + 1);
      return [
        { key: 'assets', title: 'Assets', rows: left, total: this.summary.assets, footer: 'Total Assets' },
        {
          key: 'liabilities', title: 'Liabilities & Equity', rows: right, total: this.summary.liabilities_equity,
          footer: 'Total Liabilities & Equity',
        },
      ];
    },
  },

  watch: {
    'filters.date'(date) { if (date) this.onFilterChange(); },
    'filters.depth'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { date, depth } = this.filters;
      return { ...(date ? { date } : {}), ...(depth ? { depth } : {}) };
    },

    resetExtraFilters() {
      return { date: this.today, depth: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    rowClass(row) {
      return { section: 'acc-section', group: 'acc-group', subtotal: 'acc-subtotal' }[row.kind] || '';
    },

    printReport() {
      const s = this.summary;
      const levels = DEPTH_CHOICES.find((c) => c.id === this.filters.depth)?.text;
      this.printStatement(
        'Balance Sheet',
        [`As on ${this.formatDate(this.filters.date)}`, `Levels: ${levels}`, this.storeLabel()],
        [['Assets', this.formatMoney(s.assets)], ['Liabilities', this.formatMoney(s.liabilities)],
          ['Equity', this.formatMoney(s.equity)], ['Status', s.balanced ? 'Balanced' : `Off by ${this.formatMoney(s.difference)}`]],
        [
          { label: 'Particulars', value: (r) => `${'\u00a0\u00a0\u00a0'.repeat(r.level || 0)}${r.name}` },
          { label: 'Code', value: (r) => r.code || '' },
          { label: 'Amount', value: (r) => (r.kind === 'section' || r.kind === 'spacer' ? '' : this.formatMoney(r.amount)), num: true },
        ],
        this.rows.filter((r) => r.kind !== 'spacer'),
        { Particulars: 'Total Liabilities & Equity', Amount: this.formatMoney(s.liabilities_equity) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
